/*global _*/

import axios from "axios";
import qs from 'qs';
import { getHeadersConfig } from '../../../utils/config';
import ErrorHandler from "../../../utils/ErrorHandler";
import { joinURLs } from "../../../utils/urlUtil";

/**
 * Have your way with the AppTap server!
 * A little client library to make calls
 * to AppTap server a bit easier
 */
function AppTap() {
	this.debug_flag = true;
}

/**
 * debug utility
 */
AppTap.prototype.debug = function( message ) {
	if( this.debug_flag ) {
		console.debug( message );
	}
};

/**
 * Join two paths, resolving any potential extra slashes.
 * 
 * Takes:
 * 
 *    path1
 *    path2
 * 
 * And returns:
 * 
 *    path1/path2
 * 
 * Detects if path1 ends with a slash or path2 starts with a slash and ensures only a single
 * slash is used.
 */
AppTap.prototype.join_paths = AppTap.join_paths = function(path1, path2) {
	var joined = path1.replace(/[/]+$/, "") + "/" + path2.replace(/^[/]+/, "");
	return joined;
}

AppTap.prototype.make_request = function( route, method, params, success, fail, complete, dataMunge ) {
	// strip the leading / off of the route, so that it's joined correctly to either the proxy path or the server URL
	if ( route.startsWith('/') ) {
		route = route.substr(1);
	}
	const url = joinURLs(route, process.env.REACT_APP_JUPITER_API_BASE_URL, window.location.href);

	axios({
		url: url,
		method,
		params,
		paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
		...getHeadersConfig()
	}).then(function (data) {
		data = data.data;
		if( dataMunge ) {
			data = dataMunge( data );
		}
		if( success ) {
			success( data );
		}
	}).catch(function (error) {
		const isLoggedOut = ErrorHandler.handleLoggedOutError(error);
		if (!isLoggedOut) {
			fail(error);
		}
	}).finally( function(resp) {
		if( complete ) {
			complete(resp);
		}
	});
}

/**
 * GENERIC REQUEST
 * Tries to talk directly to api.apptap.com and falls back to using the proxy URL on the app server
 * if api.apptap.com fail with a 401/403 (not authorized) or 0 (failed, likely due to CORS/not available) status
 */
AppTap.prototype.request = function( method, params, route, success, fail, complete, dataMunge, calling ) {
	var that = this;

	var path = route;
	if ( route[0] === '/' ) {
		path = path.substr(1)
	}

	// first try directly to the remote api host, if one is specified - otherwise
	// the proxy URL will be used
	if ( window.api_host ) {
		if ( window.api_host.indexOf("apptap.com") !== -1 ) {
			route = AppTap.join_paths("https://" + window.api_host, path);
		} else {
			route = AppTap.join_paths("http://" + window.api_host, path);
		}

		function is_retry_error(resp) {
			return resp.status === 403 || resp.status === 401 || resp.status === 0;
		}

		// only call the original "complete()" on success for the first attempt
		// but always call it on the second attempt
		var old_complete = complete;
		if ( old_complete ) {
			complete = function(resp) {
				if ( is_retry_error(resp) ) {
					return;
				} else {
					old_complete()
				}
			}
		}

		// wrap the fail method to allow for falling back to the proxy request if authentication fails directly to the server
		var old_fail = fail;
		fail = function( resp ) {
			// detect lack of authorization or a non-specified error (likely CORS missing headers)
			// and retry the request through the proxy endpoint
			if ( is_retry_error(resp) ) {
				route = AppTap.join_paths(window.proxy, path);

				// make the second request with the original fail and complete handlers
				that.make_request( route, method, params, success, old_fail, old_complete, dataMunge )
			} else {
				old_fail(resp);
			}
		}
	}

	this.make_request( route, method, params, success, fail, complete, dataMunge )
};

/**
 * POST REQUEST
 */
AppTap.prototype.post = function( params, route, success, fail, complete, dataMunge, calling ) {
	this.request( 'POST', params, route, success, fail, complete, dataMunge, calling );
};

/**
 * GET REQUEST
 */
AppTap.prototype.get = function( params, route, success, fail, complete, dataMunge, calling ) {
	this.request( 'GET', params, route, success, fail, complete, dataMunge, calling );
};

/**
 * Get a single clink from by clinkID
 * Requires: param:clinkid
 */
AppTap.prototype.getClink = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'getClink';
	this.get( params, route, success, fail, complete );
};

/**
 * Get clinks that have same pubID, siteID and appID
 * Requires: param:pubid, param:siteid
 * Optional: param:appid
 */
AppTap.prototype.getSimilarClinks = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	var dataMunge = function( data ) {
		data.map( function( clink ) {
			return clink;
		});
		return data;
	};
	
	params.method = 'getSimilarClinks';
	this.get( params, route, success, fail, complete, dataMunge );
};

/**
 * Get clinks that have the the same appID
 * Requires: param:appid
 */
AppTap.prototype.getClinksForApp = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	var dataMunge = function( data ) {
		data.map( function( clink ) {
			var platforms = clink.configs[0].platforms;
			if( platforms ) {
				clink.iphone  = platforms.indexOf( 'iphone' ) !== -1;
				clink.ipad    = platforms.indexOf( 'ipad' ) !== -1;
				clink.android = platforms.indexOf( 'android' ) !== -1;
			}
			clink.platforms = platforms;
			clink.status = clink.configs[0].status;
			return clink;
		});
		return data;
	};
	
	params.method = 'getClinksForApp';
	this.get( params, route, success, fail, complete, dataMunge );
};

/**
 * Remove a clink
 * Requires: param:clinkid, param:udpatedby
 */
AppTap.prototype.endClink = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'endClink';
	this.post( params, route, success, fail, complete );
};

/**
 * Add new clinks
 * Requires: param:status, param:pubid, param:siteid, param:appid
 *           param:campids, param:platforms, param:creator, param:p2p
 *           param:min, param:countries, param:updatedby, param:allowIncent
 */
AppTap.prototype.addClinks = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	params.method = 'addClinks';
	
	this.post( params, route, success, fail, complete );
};
/**
 * Add a new clink
 * Requires: param:status, param:pubid, param:siteid, param:appid
 *           param:campids, param:platforms, param:creator, param:p2p
 *           param:min, param:countries, param:updatedby
 */
AppTap.prototype.addClink = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'addClink';
	this.post( params, route, success, fail, complete );
};
/**
 * Edit an existing clink
 * Requires: params:clinkid, param:status, param:pubid, param:siteid, 
 * 			 param:appid, param:campids, param:platforms, param:creator,
 *           param:p2p, param:min, param:countries, param:updatedby
 */
AppTap.prototype.modifyClink = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'addClink';
	this.post( params, route, success, fail, complete );
};

/**
 * Updates an existing clink.  This allows only the updated clink attributes to be passed back to the API.
 * Requires: params:clinkid
 * Optional: params:allowCounterBid
 */
AppTap.prototype.updateClink = function( params, success, fail, complete ) {
	var route = '/admin/clinks';

	params.method = 'updateClink';
	this.post( params, route, success, fail, complete );
}

/**
 * Requests for a clink to be created by a reach partner.
 * Requires: param:appid, param:platforms, param:countries, param:p2p, param:allowIncent
 */
AppTap.prototype.requestClink = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'requestClink';
	this.post( params, route, success, fail, complete );
};

/**
 * Edit the status of a single clink
 * Requires: param:clinkid, param:status, param:updatedby
 */
AppTap.prototype.changeClinkStatus = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'changeClinkStatus';
	this.post( params, route, success, fail, complete );
};

/**
 * Tell the server to get a fresh copy of all clinks from mongo (or csv)
 */
AppTap.prototype.refreshClinks = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'refreshClinks';
	this.post( params, route, success, fail, complete );
};

/**
 * Get list of campaigns from AppTap inventory
 * Requires: param:search
 */
AppTap.prototype.campaigns = function( params, success, fail, complete ) {
	var route = '/admin/campaigns/query';
	params.formattedOptimizations = true;
	this.get( params, route, success, fail, complete );
};

/**
 * Get the competitive analysis for a specific campaign demand source / country 
 */
AppTap.prototype.demandSourceAnalysis = function( params, success, fail, complete ) {
	var route = '/admin/campaigns/sourceanalysis';
	
	this.get( params, route, success, fail, complete );
}

/**
 * Get list of campaigns for a specific appid only
 * Requires: param:appid
 */
AppTap.prototype.campaignsForAppID = function( params, success, fail, complete ) {
	var route = '/admin/campaigns/query';
	
	params.method = 'campaignsForAppID';
	params.returnCampaigns = true;
	params.formattedOptimizations = true;
	this.get( params, route, success, fail, complete );
};

/**
 * Get todays stats for clink
 * Requires: param:clinkid
 */
AppTap.prototype.getClinkStats = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'getClinkStats';
	this.get( params, route, success, fail, complete );
};

/**
 * Get list of demand source partners
 */
AppTap.prototype.getDemandSources = function( params, success, fail, complete ) {
	var route = '/admin/clinks';

	var dataMunge = function( data ) {
		data.map( function( source ) {
			return source.trim();
		});
		data = data.filter( function( source ) {
			// Exclude sources with a '*' or of length zero
			if( source.length === 0 || source.indexOf('*') !== -1) {
				return false;
			}
			return true;
		});
		data.sort();
		return data;
	};
	
	params.method = 'getDemandSources';
	this.get( params, route, success, fail, complete, dataMunge );
};

/**
 * Get list of publishers
 */
AppTap.prototype.getPublishers = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	var dataMunge = function( data ) {
		var ret = {};
		_.map( data.publishers, function( config ) {
			var key = config.publisherID + ":" + config.siteID;
			if( !ret[key] ) {
				ret[key] = config.publisherName;
			}
		});
		return ret;
	};
	
	params.method= 'getPublishersList';
	this.get( params, route, success, fail, complete, dataMunge, "getPublishers" );
};

/**
 * Get list of publishers
 */
AppTap.prototype.getPublishersFull = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	var dataMunge = function( data ) {
		var ret = {};
		_.map( data.publishers, function( config ) {
			var key = config.publisherID + ":" + config.siteID;
			ret[key] = config;
		});
		return ret;
	};
	
	params.method= 'getPublishersList';
	this.get( params, route, success, fail, complete, dataMunge, "getPublishersFull" );
};


/**
 * Get list of categories
 */
AppTap.prototype.getCategories = function( params, success, fail, complete ) {
	var route = '/admin/status/app/categories';
	
	var dataMunge = function( data ) {
		return data;
	};
	
	params.format = 'json';
	this.get( params, route, success, fail, complete, dataMunge, "getCategories" );
};

/**
 *  Get list of clink assignable users.
 */
AppTap.prototype.getClinkAssignableUsers = function( params, success, fail, complete ) {
	var route = '/admin/clinks';

	var dataMunge = function( data ) {
		return data;
	};

	params.method = 'getAssignableUsers';
	this.get( params, route, success, fail, complete, dataMunge, "getClinkAssignableUsers" );
};

/**
 * Get cLink links : links we give to parnter
 * Requires: param:download, param:pubID
 */
AppTap.prototype.getClinkURLs = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	params.method = 'getClinkURLs';
	params.target = 'json';
	this.get( params, route, success, fail, complete, null );
};

/**
 * Get clinks for filter criteria.
 * Parameters (one or the other):
 *   status - array of statusses
 *   filter - a filter to use (string)
 * Parameters (optional):
 *   publisherID - only return clinks matching this publisher
 *   search - only return clinks matching the search term (app name, clinkID, or appID) 
 * For Reach Parnters (non-admin) Usrs
 *   user - the publisher ID of the user
 */
AppTap.prototype.getClinks = function( params, success, fail, complete ) {
	var route = '/admin/clinks';

	params.method = 'getClinks';
	this.get( params, route, success, fail, complete, null );
};


/**
 *  Get summary of all clinks
 *  Includes: #clinks by publisher,
 *  #active, #paused, #problems
 */
AppTap.prototype.getAllClinksSummary = function( params, success, fail, complete ) {
	var route='/admin/clinks';

	params.method = 'getAllClinksSummary';
	this.get( params, route, success, fail, complete, null );
};

/**
 *	Get app info for a list of apps
 *  Requires: param:ios, param:android - list of apps like "appid,appid2,appid3"
 */
AppTap.prototype.getAppInfo = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	
	// use POST - list of appids could get pretty long

	params.method = 'getAppInfo';
	this.post( params, route, success, fail, complete, null );
};

/**
 * Sends a request to the appropriate AppTap personel for campaign creative from a reach partner.
 * Requires: param:clinkid, param:user (the user's publisherID)
 */
AppTap.prototype.requestCampaignCreative = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	params.method = "requestCreative";
	this.post( params, route, success, fail, complete, null );
};

/**
 * Unauthorize apps
 * Requires: param:iosIDs, param:andIDs, param:email, param:date 
 * 			 param:name, param:property, param:input
 */
AppTap.prototype.unauthorizeApps = function(params, success, fail, complete) {
	var route = '/admin/unauthorized_apps_util';
	
	params.method = "unauthorizeApps";
	this.post( params, route, success, fail, complete, null);
};

/**
 *  Get and relevant issues with a particular campaign
 *  Requires: param:appid, param:tracker, param:campaignid
 */
AppTap.prototype.getCampaignIssues = function( params, success, fail, complete ) {
	var route = '/admin/clinks';

	params.method = 'getCampaignIssues';
	this.get( params, route, success, fail, complete, null );
};

/**
 *  Get demands source details summary (#campaigns, geos, active, etc.)
 */
AppTap.prototype.getDemandSourceSummary = function( params, success, fail, complete ) {
	var route = '/admin/clinks';

	params.method = 'getDemandSourceSummary';
	this.get( params, route, success, fail, complete, null );
};

/**
 * Get campaign status counters
 * Requires: param:campaignid
 */
AppTap.prototype.getSummarizedStats = function( params, success, fail, complete ) {
	var route = '/admin/status/counters/summarized/data';	
	this.get( params, route, success, fail, complete, null );
};

/**
 * Get decoded IDs for an encoded cLink URL.
 * Requires: param:clinkURL
 */
AppTap.prototype.getDecodedClinkIDs = function( params, success, fail, complete ) {
    var route = '/admin/clinks';
    
    params.method = 'getDecodedClinkIDs';
    this.get( params, route, success, fail, complete, null );
};

/**
 * Requests a QC retest for the given campaign.
 * Requires: param:qc_system, param:app_id, param:demand, param:campaign_id
 */
AppTap.prototype.requestQCRetest = function( params, success, fail, complete ) {
	var route = '/admin/clinks';
	params.method = 'retestQC';
	
	this.post( params, route, success, fail, complete );
};

/**
 * Get the decoded components of an auto cLink ID from an encoded auto clink ID 
 * Requires: param:clinkid
 */
AppTap.prototype.getDecodedAutoClinkID = function( params, success, fail, complete ) {
    var route = '/admin/clinks';
	
    params.method = 'getDecodedAutoClinkID';
    this.get( params, route, success, fail, complete, null );
};

AppTap.prototype.getCookie = function( name ) {
	var value = null;
	
	try {
	    if ( document.cookie ) {
		  var cookieArray = document.cookie.split( ';' );
		  for ( var i = 0; i< cookieArray.length; i++ ) {
			  var tokens = cookieArray[i].split( '=' );
			  if ( tokens[0].trim() === name ) {
				  value = tokens[1].trim();
				  break;
			  }
		  }
	    }
	}
	catch ( exception ) {
	    console.error( "Error loading cookie: " + exception.message );	
	}
	
    return value;
};

AppTap.prototype.setCookie = function( name, value ) {
	try {
	    var expirationDate = new Date();
	    expirationDate.setFullYear( expirationDate.getFullYear() + 10 );
	    
		var cookieToAdd = name + '=' + value + '; expires=' + expirationDate.toGMTString() + '; path=/;';
		document.cookie = cookieToAdd;
	}
	catch ( exception ) {
	    console.error( "Error setting cookie: " + exception.message );	
	}
};

AppTap.prototype.objectToKeyValueArray = function( objectArray ) {
	var ret = [];
	for ( var i in objectArray ) {
		ret.push( { key: i, value: objectArray[i] } );		
	}
	return ret;
}

AppTap.prototype.filterStatsByPercent = function( statsArray ) {
	if ( statsArray.length <= 10 ) return statsArray;
	
	var other = {
		key: 'OTHER',
		keys: [],
		value: {
			'installs': 0,
			'buys': 0
		}
	}
	
	var ret =[];
	statsArray.forEach(function(s) {
		if ( s.value.buysPercent >= 0.5 || s.value.installsPercent >= 0.5 ) {
			ret.push( s );
		} else {
			other.keys.push( s.key );
			other.value.installs        += s.value.installs;
			other.value.buys            += s.value.buys;
			other.value.installsPercent += s.value.installsPercent;
			other.value.buysPercent     += s.value.buysPercent;
		}
	});

	// if only one got excluded, push it back in as normal
	var joinedKeys = other.keys.join(", ");
	if ( other.keys.length === 1 ) {
		other.key = other.keys[0];
	} else if ( other.keys.length > 5 || joinedKeys.length > 20 ) {
		other.key = "OTHERS (" + other.keys.length + ")";
	} else {
		other.key = "OTHERS (" + joinedKeys + ")";
	}
	ret.push( other );
	
	return ret;
};

export default AppTap;